import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Typography, IconButton, Chip, useTheme } from '@mui/material';
import { Pencil, Trash2, Sparkles, GripVertical, Calculator } from 'lucide-react';
import { AIFieldNodeData } from '../../types/form';

const AI_COLOR = '#7C3AED';

const AIFieldNode: React.FC<NodeProps<AIFieldNodeData>> = ({ data }) => {
  const theme = useTheme();
  const { field, onEdit, onDelete } = data;

  // Extract referenced field IDs from the prompt
  const referencedIds = [
    ...new Set((field.aiPrompt || '').match(/\{(\w+)\}/g)?.map(m => m.slice(1, -1)) || []),
  ];

  // Truncate prompt for display
  const truncatedPrompt =
    field.aiPrompt && field.aiPrompt.length > 80
      ? field.aiPrompt.substring(0, 80) + '...'
      : field.aiPrompt || '';

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `2px solid ${AI_COLOR}`,
        borderRadius: 2,
        minWidth: 300,
        maxWidth: 400,
        boxShadow: theme.shadows[1],
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: AI_COLOR,
          width: 8,
          height: 8,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: `1px solid ${AI_COLOR}30`,
          backgroundColor: `${AI_COLOR}10`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GripVertical size={14} color={theme.palette.text.secondary} style={{ cursor: 'grab' }} />
          <Sparkles size={14} color={AI_COLOR} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: AI_COLOR }}>
            AI Prompt
          </Typography>
          {field.showResultToUser === false && (
            <Chip
              icon={<Calculator size={10} />}
              label="Silent"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 600,
                backgroundColor: `${AI_COLOR}20`,
                color: AI_COLOR,
                '& .MuiChip-icon': { color: AI_COLOR, ml: 0.5 },
              }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            #{field.order}
          </Typography>
        </Box>
        <Box>
          <IconButton size="small" onClick={() => onEdit(field.fieldId)} sx={{ mr: 0.5 }}>
            <Pencil size={14} />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(field.fieldId)}>
            <Trash2 size={14} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {field.label || 'Untitled AI Field'}
        </Typography>

        {truncatedPrompt && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mb: 1,
              fontStyle: 'italic',
              lineHeight: 1.4,
            }}
          >
            {truncatedPrompt}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={field.fieldId}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
          {referencedIds.map(refId => (
            <Chip
              key={refId}
              label={`{${refId}}`}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 20,
                backgroundColor: `${AI_COLOR}15`,
                color: AI_COLOR,
                borderColor: `${AI_COLOR}40`,
              }}
              variant="outlined"
            />
          ))}
        </Box>
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: AI_COLOR,
          width: 8,
          height: 8,
        }}
      />
    </Box>
  );
};

export default memo(AIFieldNode);
